import React from 'react';

import {
  FormInput,
  SubsectionForm,
  FormRow,
  FormColumn,
} from '../../../../components/forms';
import { PermissionAwareField } from '../../../../components/PermissionAwareField';
import { SECTION_IDS } from '../../../../constants/sectionIds';

import type { SelectedFile } from '../../../../components/forms/FileUpload';
import type { SavedAttachment } from '../../../../services/attachmentService';
import type { Request } from '../../../../types';

type MonitoringManagementAttachmentBundle = {
  attachmentsBySection: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
};

export type MonitoringManagementInformationProps =
  MonitoringManagementAttachmentBundle &
    (
      | {
          mode: 'initial';
          formData: Record<string, unknown>;
          updateFormData: (field: string, value: string) => void;
          requestId?: string;
          disabled: boolean;
          isEditing: boolean;
        }
      | {
          mode: 'persisted';
          request: Request;
          isEditing: boolean;
          editedData: Record<string, unknown>;
          updateEditedData: (field: string, value: string) => void;
          showFullDetails?: boolean;
        }
    );

/**
 * Main “Monitoring Management” card for create and opened-request layouts.
 */
export const MonitoringManagementInformation: React.FC<
  MonitoringManagementInformationProps
> = props => {
  const {
    attachmentsBySection,
    onAttachFiles,
    onDeleteAttachment,
    selectedFiles,
    onFilesSelected,
    offlineMode,
    onCaptionChange,
    pendingCaptionEdits,
  } = props;

  if (props.mode === 'initial') {
    const { formData, updateFormData, requestId, disabled, isEditing } = props;
    const stage = formData.stage ?? formData.sysRequestStage;
    return (
      <SubsectionForm
        title="Monitoring Management Information"
        sectionId={SECTION_IDS.MONITORING_INFO}
        requestId={requestId}
        allowAttachments={true}
        attachments={attachmentsBySection[SECTION_IDS.MONITORING_INFO] || []}
        onAttachFiles={() => onAttachFiles?.(SECTION_IDS.MONITORING_INFO)}
        onDeleteAttachment={onDeleteAttachment}
        isEditing={isEditing || !disabled}
        disabled={disabled}
        selectedFiles={selectedFiles[SECTION_IDS.MONITORING_INFO] || []}
        onFilesSelected={
          onFilesSelected
            ? files => onFilesSelected(SECTION_IDS.MONITORING_INFO, files)
            : undefined
        }
        isOffline={offlineMode}
      >
        <FormRow>
          <FormColumn>
            <PermissionAwareField
              fieldId="mgmtDescribeMonitoringControlSoftwareNeeds"
              requestStage={stage}
              requestId={requestId}
            >
              <FormInput
                label="Describe Monitoring & Control Software needs"
                value={String(
                  formData.mgmtDescribeMonitoringControlSoftwareNeeds ?? '',
                )}
                onChangeText={value =>
                  updateFormData(
                    'mgmtDescribeMonitoringControlSoftwareNeeds',
                    value,
                  )
                }
                placeholder="Describe monitoring and control software requirements"
                multiline
                maxLength={10000}
                richText
                requestId={requestId}
                requestSection={SECTION_IDS.MONITORING_INFO}
                isOnline={!offlineMode}
                disabled={disabled}
                readOnlyMode={disabled}
              />
            </PermissionAwareField>
          </FormColumn>
        </FormRow>
      </SubsectionForm>
    );
  }

  const {
    request,
    isEditing,
    editedData,
    updateEditedData,
    showFullDetails = true,
  } = props;
  const reqRec = request as unknown as Record<string, unknown>;

  const getValue = (field: string) => {
    const raw = isEditing ? editedData[field] ?? reqRec[field] : reqRec[field];
    if (raw === undefined || raw === null) return '';
    return String(raw);
  };

  return (
    <SubsectionForm
      title="Monitoring Management"
      alwaysShow={true}
      sectionId={SECTION_IDS.MONITORING_INFO}
      requestId={request.id}
      allowAttachments={true}
      attachments={attachmentsBySection[SECTION_IDS.MONITORING_INFO] || []}
      onAttachFiles={() => onAttachFiles?.(SECTION_IDS.MONITORING_INFO)}
      onDeleteAttachment={onDeleteAttachment}
      isEditing={isEditing}
      disabled={!isEditing}
      readOnlyMode={!isEditing}
      selectedFiles={selectedFiles[SECTION_IDS.MONITORING_INFO] || []}
      onFilesSelected={
        onFilesSelected
          ? files => onFilesSelected(SECTION_IDS.MONITORING_INFO, files)
          : undefined
      }
      isOffline={offlineMode}
      onCaptionChange={onCaptionChange}
      pendingCaptionEdits={pendingCaptionEdits}
    >
      <PermissionAwareField
        fieldId="mgmtDescribeMonitoringControlSoftwareNeeds"
        requestStage={request.stage || request.sysRequestStage}
        requestId={request.id}
        showFullDetails={showFullDetails}
      >
        <FormInput
          label="Describe Monitoring and Control Software Needs"
          value={getValue('mgmtDescribeMonitoringControlSoftwareNeeds')}
          onChangeText={
            isEditing
              ? value =>
                  updateEditedData(
                    'mgmtDescribeMonitoringControlSoftwareNeeds',
                    value,
                  )
              : () => {}
          }
          placeholder="Enter monitoring and control software needs"
          multiline
          maxLength={10000}
          richText
          requestId={String(request.id)}
          requestSection={SECTION_IDS.MONITORING_INFO}
          isOnline={!offlineMode}
          disabled={!isEditing}
          readOnlyMode={!isEditing}
        />
      </PermissionAwareField>
    </SubsectionForm>
  );
};

MonitoringManagementInformation.displayName = 'MonitoringManagementInformation';
